import { Button } from '@acpaas-ui/react-components';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import DossierClarificationAttachments from '../../../../components/dossier-clarification/DossierClarificationAttachments';
import DossierClarificationStakeholders from '../../../../components/dossier-clarification/DossierClarificationStakeholders';
import DossierSectionWrapper from '../../../../components/dossier-details/DossierSectionWrapper';
import DossierSubitemActionBar from '../../../../components/dossier-shared/DossierSubitemActionBar';
import { STAKEHOLDER_DOSSIER_PATHS } from '../../../../constants/dossier.constants';
import { MODALS } from '../../../../constants/modals.constants';
import { PERMISSIONS } from '../../../../constants/permissions.constants';
import { ATTACHMENT_TYPES, TOAST_TYPES } from '../../../../constants/settings.constants';
import { useAttachmentCRUD } from '../../../../hooks/useAttachmentCRUD.hook';
import { useModal } from '../../../../hooks/useModal.hook';
import {
  canAddClarification,
  canDeleteClarification,
  clarificationToTitle,
  getAttachmentsForClarification
} from '../../../../lib/dossier.utils';
import AttachmentDetailsModal from '../../../../modals/AttachmentDetailsModal';
import AttachmentUploadModal from '../../../../modals/AttachmentUploadModal';
import ClarificationApproveModal from '../../../../modals/clarification/ClarificationApproveModal';
import DeleteWarningModal from '../../../../modals/DeleteWarningModal';
import StakeholderAddModal from '../../../../modals/StakeholderAddModal';
import StakeholderDetailModal from '../../../../modals/StakeholderDetailModal';
import { hasPermission } from '../../../../selectors/auth.selectors';
import clarificationsService from '../../../../services/api/clarifications.service';
import { clarifications } from '../../../../services/bff.api';
import clarificationPermissionsService from '../../../../services/permissions/dossier.tabs.permissions.service';
import permissionsService from '../../../../services/permissions/permissions.service';
import responseValidators from '../../../../services/response.validators';
import toastService from '../../../../services/toasts.service';
import DossierClarificationDetailsContainer from './DossierClarificationDetailsContainer';

const DossierDetailClarification = ({
  data,
  isLoadingAttachments,
  onUpdated,
  onAttachmentsRefetch,
  onDossierRefetch,
  attachments,
  userRoles,
  openedClarifications,
  setOpenedClarifications,
  permissions,
  user
}) => {
  const params = useParams();

  const attachmentCRUD = useAttachmentCRUD(params.id);
  const deleteModal = useModal(MODALS.CLARIFICATION_CONFIRM_DELETE);
  const approveModal = useModal(MODALS.CLARIFICATION_APPROVE);
  const stakeholderAddModal = useModal(MODALS.STAKEHOLDER_ADD_MODAL);
  const stakeholderDetailModal = useModal(MODALS.STAKEHOLDER_DETAIL_MODAL);

  const [stakeholderClarificationId, setStakeholderClarificationId] = useState('');
  const [stakeholderDetailModalData, setStakeholderDetailModalData] = useState({});
  const [modalPermissions, setModalPermissions] = useState({
    write: false,
    read: false
  });
  const [deletingClarificationId, setDeletingClarificationId] = useState('');
  const [approvingClarificationId, setApprovingClarificationId] = useState('');
  const [creatingClarification, setCreatingClarification] = useState(false);

  const handleAdd = () => {
    setCreatingClarification(true);
    clarificationsService.add(params.id, {
      onSuccess: (resp) => {
        setCreatingClarification(false);
        onDossierRefetch();
        setOpenedClarifications([resp.id]);
      },
      onError: (message) => {
        toastService.showToast(TOAST_TYPES.ERROR, message);
        setCreatingClarification(false);
      }
    });
  };

  const handleRequestDelete = (id) => {
    setDeletingClarificationId(id);
    deleteModal.onOpen();
  };

  const handleDelete = (onError, onSuccess) => {
    clarifications
      .delete(params.id, deletingClarificationId)
      .then((resp) => {
        responseValidators.deleteClarification(resp);
        onSuccess();
        onDossierRefetch();
      })
      .catch((err) => {
        onError(err.message);
      });
  };

  const handleRequestApprove = (id) => {
    setApprovingClarificationId(id);
    approveModal.onOpen();
  };

  const handleApprove = (remark, onError, onSuccess) => {
    clarifications
      .approve(params.id, approvingClarificationId, { message: remark })
      .then((resp) => {
        responseValidators.approveClarification(resp);
        onSuccess();
        onDossierRefetch();
      })
      .catch((err) => {
        onError(err.message);
      });
  };

  const handleUpdated = (value, field, clarificationId) => {
    onUpdated(value, field, 'requestClarification', clarificationId);
  };

  const handleStakeholderAddClick = (clarificationId) => {
    setStakeholderClarificationId(clarificationId);
    stakeholderAddModal.onOpen();
  };

  const handleStakeholderOpenClick = (clarificationId, data, permissions) => {
    if (!permissions?.read) {
      return;
    }
    setStakeholderClarificationId(clarificationId);
    setStakeholderDetailModalData(data);
    setModalPermissions(permissions);
    stakeholderDetailModal.onOpen();
  };

  const handleAttachmentOpenClick = (attachment, permissions) => {
    if (!permissions.read) {
      return;
    }
    setModalPermissions(permissions);
    attachmentCRUD.onAttachmentOpen(attachment);
  };

  const getCanDelete = () => {
    return canDeleteClarification(data?.status, userRoles);
  };

  const getCanApprove = (c) => {
    return (
      !c.approvedDate &&
      !isLoadingAttachments &&
      getAttachmentsForClarification(c, attachments).length > 0 &&
      c.date &&
      c.startTime &&
      c.endTime
    );
  };

  const getCanShare = getCanApprove;

  const descriptionLabel = 'Wat is de specifieke ondersteuningsvraag van de school?';
  const descriptionDescription = 'Jouw antwoord zal getoond worden in de tab “Casetafel”.';

  return (
    <div>
      <DossierSectionWrapper
        title="Vraagverhelderingen"
        noItemsLabel="Er zijn geen vraagverhelderingen toegevoegd."
        onAddItem={canAddClarification(data?.status, userRoles) ? handleAdd : undefined}
        addLabel="Vraagverheldering toevoegen"
        openedItemIds={openedClarifications}
        onItemHeaderClick={(open, id) => {
          const newValue = open
            ? [...(openedClarifications ?? []), id]
            : openedClarifications?.filter((oc) => oc !== id) ?? [];
          setOpenedClarifications(newValue);
        }}
        isAdding={creatingClarification}
        items={
          data?.requestClarification?.map((clarification) => {
            const clarificationPermissions = clarificationPermissionsService.toClarificationPermissions(
              clarification,
              permissions,
              user
            );
            return {
              id: clarification.id,
              title: clarificationToTitle(clarification),
              Component: (
                <>
                  <DossierClarificationStakeholders
                    {...clarification}
                    {...clarificationPermissions}
                    onStakeholderOpen={(stakeholder) =>
                      handleStakeholderOpenClick(clarification.id, stakeholder, clarificationPermissions)
                    }
                    onStakeholderAdd={() => handleStakeholderAddClick(clarification.id)}
                  />
                  <DossierClarificationDetailsContainer
                    {...clarificationPermissions}
                    clarification={clarification}
                    dossierId={params.id}
                    onUpdated={handleUpdated}
                  />
                  <DossierClarificationAttachments
                    {...clarificationPermissions}
                    loadingAttachments={isLoadingAttachments}
                    dossierType={data?.type}
                    attachments={getAttachmentsForClarification(clarification, attachments)}
                    onAttachmentAdd={() =>
                      attachmentCRUD.onAttachmentAddOpen(clarification.id, ATTACHMENT_TYPES.CLARIFICATION)
                    }
                    onAttachmentOpen={(att) => handleAttachmentOpenClick(att, clarificationPermissions)}
                  />
                  <DossierSubitemActionBar
                    {...clarification}
                    {...clarificationPermissions}
                    actions={
                      (clarificationPermissions.write ||
                        (permissionsService.isStakeholderInTab(user, clarification?.involved) &&
                          hasPermission(user, PERMISSIONS.SCHOOL_ADMIN))) &&
                      getCanApprove(clarification) && (
                        <Button
                          className="u-margin-right-sm"
                          type="success"
                          iconRight="ai-check-1"
                          onClick={() => handleRequestApprove(clarification.id)}
                        >
                          Goedkeuren
                        </Button>
                      )
                    }
                    openedClarificationId={clarification.id}
                    canDelete={clarificationPermissions.write && getCanDelete()}
                    canShare={
                      (clarificationPermissions.write ||
                        (permissionsService.isStakeholderInTab(user, clarification?.involved) &&
                          hasPermission(user, PERMISSIONS.SCHOOL_ADMIN))) &&
                      getCanShare(clarification, clarificationPermissions)
                    }
                    onDelete={handleRequestDelete}
                    mailToLink={`mailto:?subject=${encodeURIComponent('Verslag Vraagverheldering')}`}
                  />
                </>
              )
            };
          }) ?? []
        }
      />
      <AttachmentUploadModal
        {...attachmentCRUD.uploadModal}
        onConfirm={attachmentCRUD.onAttachmentAdd}
        descriptionLabel={descriptionLabel}
        descriptionDescription={descriptionDescription}
      />
      <AttachmentDetailsModal
        {...attachmentCRUD.detailsModal}
        {...modalPermissions}
        dossierId={params.id}
        form={attachmentCRUD.attachmentDetailForm}
        onUpdate={attachmentCRUD.onAttachmentUpdate}
        onUpdateSuccess={onAttachmentsRefetch}
        onDelete={attachmentCRUD.deleteModal.onOpen}
        descriptionLabel={descriptionLabel}
        descriptionDescription={descriptionDescription}
      />
      <ClarificationApproveModal {...approveModal} onConfirm={handleApprove} />
      <StakeholderAddModal
        {...stakeholderAddModal}
        dossierSchools={data?.overview?.school ?? []}
        onUpdated={handleUpdated}
        dossierId={params.id}
        dossierPath={STAKEHOLDER_DOSSIER_PATHS.CLARIFICATION}
        itemId={stakeholderClarificationId}
      />
      {stakeholderDetailModal.isOpen && (
        <StakeholderDetailModal
          {...stakeholderDetailModal}
          {...modalPermissions}
          data={stakeholderDetailModalData}
          onUpdated={handleUpdated}
          dossierId={params.id}
          dossierPath={STAKEHOLDER_DOSSIER_PATHS.CLARIFICATION}
          itemId={stakeholderClarificationId}
        />
      )}
      {attachmentCRUD.deleteModal.isOpen && (
        <DeleteWarningModal
          {...attachmentCRUD.deleteModal}
          warningText="Weet je zeker dat je deze bijlage wilt verwijderen?"
          onConfirm={attachmentCRUD.onAttachmentDelete}
        />
      )}
      <DeleteWarningModal
        {...deleteModal}
        warningText="Ben je zeker dat je deze vraagverheldering wilt verwijderen? Alle informatie die hieraan gekoppeld is zal verloren gaan."
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default DossierDetailClarification;
